/* eslint-disable max-lines-per-function */
import { useMemo } from 'react';

import JSONCrush from 'jsoncrush';

import type { SessionUser } from 'src/shared/session';

import { safeParse } from 'src/app/pages/startAPetition/shared/utils';
import { useQueryParamValue } from 'src/app/shared/hooks/location';
import { fromBase64Url } from 'src/app/shared/utils/base64url';

import { ALL_CONFIGS } from '../../configs';
import { publish } from '../../configs/publish';
import type { SerializedStepState, StepConfig, StepState } from '../../types';

export function useQueryParamState(
	previousStepState: SerializedStepState | undefined,
	user: SessionUser | undefined,
): StepState | undefined {
	const queryParam = useQueryParamValue('state');

	// decode the query param
	const sapQueryParam = useMemo(() => {
		if (queryParam) {
			const uncrushedString = JSONCrush.uncrush(fromBase64Url(queryParam));
			// TODO: validate uncrushed json object using zod. Ticket: https://change.atlassian.net/browse/CHANGE-66755
			return safeParse<SerializedStepState>(uncrushedString);
		}
		return undefined;
	}, [queryParam]);

	// deserializes the query param object (logic is separate from the `deserializedSteps` because of
	// different object structure, this function will return an array of steps and
	// update `step` to `name` and `requireAuth` to `isAuthenticated`
	const getQuerySteps = (steps: SerializedStepState['metadata']['steps']) => {
		const currentSteps = steps.reduce<StepConfig[]>((previousValue, currentValue) => {
			const stepConfig = ALL_CONFIGS.find((config) => config.name === currentValue.name);
			if (!stepConfig) return previousValue;
			if (!currentValue.isAuthenticationRequired || stepConfig.isAuthenticationRequired)
				return [...previousValue, stepConfig];
			return [...previousValue, { ...stepConfig, isAuthenticationRequired: currentValue.isAuthenticationRequired }];
		}, []);
		return [...currentSteps, publish];
	};

	// because this function accepts the previous step state (grabbed from local storage)
	// this object already has the fields updated: step -> name and requireAuth -> isAuthenticationRequired
	// so this just checks the previous steps exist in ALL_CONFIGS
	// and adds isEnabled function to each step
	const deserializedSteps = (stepState: SerializedStepState['metadata']['steps']) => {
		const steps = stepState.reduce<StepConfig[]>((previousValue, currentValue) => {
			const stepConfig = ALL_CONFIGS.find((config) => config.name === currentValue.name);
			if (!stepConfig) return previousValue;
			if (!currentValue.isAuthenticationRequired || stepConfig.isAuthenticationRequired)
				return [...previousValue, stepConfig];
			return [...previousValue, { ...stepConfig, isAuthenticationRequired: currentValue.isAuthenticationRequired }];
		}, []);
		return steps;
	};

	const hasQueryParam = !!sapQueryParam;
	const hasPreviousStepState = !!previousStepState;

	// if there is no query param, don't return any state
	if (!hasQueryParam) return undefined;

	// if there is no previous step state, initialize with the query param state
	if (!hasPreviousStepState) {
		return {
			...sapQueryParam,
			id: sapQueryParam.id,
			metadata: { ...sapQueryParam.metadata, steps: getQuerySteps(sapQueryParam.metadata.steps) },
		};
	}

	// clear step state and initialize with the query param state if:
	// - query param id does not match the stored id
	// - current user id does not match the stored user id
	const didQueryIdChange = sapQueryParam.id !== previousStepState.id;
	const didUserIdChange = previousStepState.metadata.user.id && previousStepState.metadata.user.id !== user?.id;
	if (didQueryIdChange || didUserIdChange) {
		return {
			...sapQueryParam,
			id: sapQueryParam.id,
			metadata: { ...sapQueryParam.metadata, steps: getQuerySteps(sapQueryParam.metadata.steps) },
		};
	}

	// else return the previous state and reinitialize the stored steps
	return {
		...previousStepState,
		metadata: { ...previousStepState.metadata, steps: deserializedSteps(previousStepState.metadata.steps) },
	};
}
